/**
 * Partner SSO launch page - /launch?token=<signed_launch_token>
 *
 * A partner redirects their user here after calling POST /pos/v1/launch-tokens
 * from their backend. This page:
 *   1. Extracts the token from the query string.
 *   2. POSTs to /pos/v1/public/launch-exchange to verify + provision + get tokens.
 *   3. Stores the resulting Cognito tokens in localStorage (same keys as the
 *      normal SRP sign-in flow so RequireAuth / readAccessToken work unchanged).
 *   4. Redirects to /sale (or a `return_to` query param if one was provided).
 *
 * On any error the user lands on /login with an explanatory query param so
 * the login page can surface a human-readable message.
 */

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const ACCESS_KEY = "tilt-pos.access_token";
const ID_KEY = "tilt-pos.id_token";

function storeTokens(accessToken: string, idToken: string) {
  localStorage.setItem(ACCESS_KEY, accessToken);
  localStorage.setItem(ID_KEY, idToken);
}

export function LaunchPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = params.get("token");
    const returnTo = params.get("return_to") || "/sale";

    if (!token) {
      navigate("/login?error=missing_launch_token", { replace: true });
      return;
    }

    const apiBase = import.meta.env.VITE_API_BASE_URL ?? "";

    fetch(`${apiBase}/pos/v1/public/launch-exchange`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body?.detail ?? body?.error ?? `HTTP ${res.status}`);
        }
        return res.json() as Promise<{
          access_token: string;
          id_token: string;
          refresh_token: string;
          expires_in: number;
        }>;
      })
      .then(({ access_token, id_token }) => {
        storeTokens(access_token, id_token);
        // Sanitise return_to - only allow relative paths to prevent open redirects.
        const safe = returnTo.startsWith("/") ? returnTo : "/sale";
        navigate(safe, { replace: true });
      })
      .catch((err: Error) => {
        setError(err.message);
        setTimeout(
          () => navigate(`/login?error=${encodeURIComponent(err.message)}`, { replace: true }),
          2000,
        );
      });
    // Run once on mount - deps intentionally empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8 text-center">
        <div>
          <p className="text-sm font-medium text-red-600">Sign-in failed</p>
          <p className="mt-1 text-xs text-gray-500">{error}</p>
          <p className="mt-1 text-xs text-gray-400">Redirecting to login…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-8 text-center">
      <div>
        <p className="text-sm text-gray-500">Signing you in…</p>
      </div>
    </div>
  );
}
